import {
  Button,
  Callout,
  Divider,
  Menu,
  MenuDivider,
  MenuItem,
  NonIdealState,
  Popover,
} from '@blueprintjs/core';
import panelClasses from '../Panel/Panel.module.css';
import { useContext, useMemo } from 'react';
import noCallsSVG from '../../../assets/noCalls.svg';
import {
  lock_hooks,
  useApplicationDetail,
  useCommentTemplates,
  ace_hooks,
  comment_hooks,
  useCustomer,
  useGetConsentPartners,
  useGetConsentItemsByCustomer,
} from '@axo/shared/data-access/hooks';
import { ace, comment } from '@axo/shared/data-access/types';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { ICommentState, useCommentsState } from './useCommentsState';
import { CommentView } from './CommentView';
import { MarketCountryCode } from '@axo/shared/types';
import { convertToE164 } from '@axo/shared/util/formatters';
import { RecordCardView } from './RecordCardView';
import { usePersistedState } from '../../../hooks/utils/usePersistedState';
import { CallOrderCard } from './CallOrderCard';
import {
  useApplicationBucket,
  useBucket,
} from '@axo/shared/data-access/hooks/services/ace';
import { UserCommentCardView } from './UserCommentCardView';
import { useCallActions } from './useCallActions';
import { toValidDate } from './utils/toValidDate';

interface Props {
  applicationID: string;
}

type Item =
  | {
      type: 'call-record';
      timestamp?: Date;
      record: ace.CallRecord;
      comments: ICommentState[];
    }
  | { type: 'comment'; timestamp?: Date; comment: ICommentState }
  | { type: 'user-comment'; timestamp?: Date; comment: comment.UserComment };

export function CallsAndCommentsPanel({ applicationID }: Props) {
  const { state } = useContext(DataAccessContext);

  const application = useApplicationDetail(applicationID);
  const bucketDetails = useApplicationBucket(applicationID);
  const bucket = useBucket(bucketDetails.data?.BucketID ?? '');

  const commentsState = useCommentsState(applicationID);
  const commentTemplates = useCommentTemplates(application.data?.MarketCountry);
  const userComments = comment_hooks.useUserComments(applicationID);

  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    application.data?.MarketCountry
  );

  const customer = useCustomer(application.data?.CustomerID);

  const internationalNumber = useMemo(
    () =>
      application.data && customer.data
        ? convertToE164({
            phoneNumber: customer.data.MobilePhoneNumber,
            phoneCountryCode: String(customer.data.MobilePhoneCountryCode),
          })
        : undefined,
    [application.data, customer.data]
  );

  const callRecords = ace_hooks.useCallRecords({
    LoanApplicationID: applicationID,
  });

  const callTags = ace_hooks.useCallTags(
    application.data?.MarketCountry as MarketCountryCode | undefined,
    state.user.roles.map((role) => role.Name)
  );

  // Keep selected outbound number type persisted in local storage
  // so sales does not have to switch manually for each application.
  const [outboundNumberType, setOutboundNumberType] =
    usePersistedState<ace.OutboundNumberType>(
      'ace.calls.outboundnumbertype.v1',
      'broker',
      window.localStorage
    );

  const consentPartners = useGetConsentPartners();
  const partnerID = useMemo(
    () =>
      consentPartners.data?.find(
        (x) =>
          x.BrandID === application.data?.Meta?.Brand &&
          Boolean(application.data.Meta.Brand)
      )?.ID,
    [consentPartners.data, application.data?.Meta?.Brand]
  );
  const consentItems = useGetConsentItemsByCustomer(
    application.data?.MarketCountry as MarketCountryCode,
    application.data?.CustomerID
  );

  // FIXME: This is a hotfix until correct behavior is determined
  const hasPhoneConsent =
    true || // eslint-disable-line
    consentItems.data?.Items?.find(
      (x) =>
        x.PartnerID === partnerID &&
        x.Country === application.data?.MarketCountry &&
        x.Transactional &&
        x.Channel === 'phone' &&
        x.State === 'given'
    );

  const items = useMemo<Item[]>(() => {
    const recordsItems: Item[] =
      callRecords.data?.Items?.map((record) => ({
        type: 'call-record',
        comments: commentsState.state
          .filter((comment) => comment.callID === record.ID)
          .sort(
            (a, b) =>
              +(a.original ? new Date(a.original.CreatedAt) : Infinity) -
              +(b.original ? new Date(b.original.CreatedAt) : Infinity)
          ),
        record,
        timestamp: new Date(record.CreatedAt),
      })) ?? [];

    const commentItems: Item[] = commentsState.state
      .filter((comment) => !comment.callID)
      .map((comment) => ({
        type: 'comment',
        timestamp: comment.original
          ? new Date(comment.original.CreatedAt)
          : undefined,
        comment,
      }));

    const userCommentItems: Item[] =
      userComments.data?.map((uc) => ({
        type: 'user-comment' as const,
        // A malformed/missing CreatedAt sorts to the OLDEST slot (epoch) rather
        // than floating to the top via the comparator's `?? Infinity` fallback
        // (which is reserved for unsaved, in-progress agent comments).
        timestamp: toValidDate(uc.CreatedAt) ?? new Date(0),
        comment: uc,
      })) ?? [];

    return recordsItems
      .concat(commentItems)
      .concat(userCommentItems)
      .sort((a, b) => +(b.timestamp ?? Infinity) - +(a.timestamp ?? Infinity));
  }, [callRecords.data?.Items, commentsState.state, userComments.data]);

  const {
    onClickCallTag,
    onClickNoCallTag,
    onClickCommentTemplate,
    isPending,
  } = useCallActions({
    applicationID,
    application: application.data,
    customer: customer.data,
    bucketName: bucket.data?.Name ?? '',
    internationalNumber,
    outboundNumberType,
    clientID: state.user.clientID,
    hasLock: !!hasLock.data,
    commentsState,
  });

  return (
    <div className={panelClasses.panel}>
      <div className={panelClasses.panelActions}>
        <Popover
          interactionKind="hover"
          content={
            <Menu>
              <MenuDivider title="Select a template" />
              {commentTemplates.data?.Items.map((template) => (
                <MenuItem
                  key={template.ID}
                  text={template.Name}
                  onClick={() => onClickCommentTemplate(template)}
                />
              ))}
            </Menu>
          }
        >
          <Button icon="chat" text="New comment" />
        </Popover>

        <Popover
          disabled={!hasLock.data}
          interactionKind="hover"
          content={
            <Menu>
              <MenuDivider title="Select call type" />

              {callTags?.data?.map((tag) => (
                <MenuItem
                  disabled={!hasPhoneConsent || isPending}
                  key={tag.ID}
                  text={tag.Name}
                  onClick={() => onClickCallTag(tag)}
                />
              ))}

              {callTags?.data?.map((tag) => (
                <MenuItem
                  disabled={isPending}
                  key={`${tag.ID}-no-call`}
                  text={`${tag.Name} (No call)`}
                  onClick={() => onClickNoCallTag(tag)}
                />
              ))}
            </Menu>
          }
        >
          <Button
            disabled={!hasLock.data}
            intent="success"
            icon="phone"
            text="New call"
          />
        </Popover>
      </div>
      <Divider />

      <div className={panelClasses.cardContainer}>
        <CallOrderCard
          type={outboundNumberType}
          setType={setOutboundNumberType}
        />

        {callRecords.isLoading ||
        !commentsState.hasLoaded ||
        userComments.isLoading ? (
          <NonIdealState>
            <p>Loading calls...</p>
          </NonIdealState>
        ) : items && !items.length ? (
          <NonIdealState icon={<img src={noCallsSVG} alt="No calls" />}>
            <p>There are no calls added yet.</p>
          </NonIdealState>
        ) : null}

        {userComments.isError && (
          <Callout
            intent="warning"
            icon="warning-sign"
            title="Customer comments could not be loaded"
          >
            <p>
              Any book-a-call comments the customer left are missing from this
              timeline.
            </p>
            <Button
              minimal
              small
              intent="warning"
              text="Retry"
              onClick={() => userComments.refetch()}
            />
          </Callout>
        )}

        {items?.map((item) => {
          switch (item.type) {
            case 'call-record': {
              const { record } = item;
              return (
                <RecordCardView
                  record={record}
                  key={record.ID}
                  disabled={state.user.clientID !== record.ClientID}
                  tags={callTags.data ?? []}
                  comments={item.comments}
                  commentTemplates={commentTemplates}
                  commentsState={commentsState}
                />
              );
            }

            case 'comment': {
              return (
                <CommentView
                  key={item.comment.original?.ID ?? 'new-comment'}
                  comment={item.comment}
                  commentsState={commentsState}
                />
              );
            }

            case 'user-comment': {
              return (
                <UserCommentCardView
                  key={`user-comment-${item.comment.ID}`}
                  comment={item.comment}
                />
              );
            }
          }
        })}
      </div>
    </div>
  );
}
